import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { styled } from '../../theme'

export type OverflowListProps<T> = {
  items: T[]
  itemsRenderer: (items: T[]) => React.ReactNode
  overflowRenderer: (items: T[]) => React.ReactNode
  className?: string
  minVisibleItems?: number
}

type OverflowListState<T> = {
  visible: T[]
  overflow: T[]
}

const CANARY_WIDTH = 1

const Container = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
`
const Canary = styled('div')`
  width: ${CANARY_WIDTH}px;
  flex-shrink: 999999;
`

export function OverflowList<T>(props: OverflowListProps<T>) {
  const { items, overflowRenderer, itemsRenderer, className, minVisibleItems = 1 } = props

  const [{ visible, overflow }, setState] = useState<OverflowListState<T>>({
    visible: items,
    overflow: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const canaryRef = useRef<HTMLDivElement>(null)

  const [width, setWidth] = useState<number>()
  const previousWidth = useRef<number>()

  const resetItems = useCallback(() => {
    setState(() => ({
      visible: items,
      overflow: [],
    }))
  }, [items])

  const hideLastItem = useCallback(() => {
    setState((state) => {
      const _visible = state.visible.slice()
      if (_visible.length <= minVisibleItems) {
        return state
      }

      const next = _visible.pop()
      if (!next) {
        return state
      }

      const _overflow = [next, ...state.overflow]
      return { visible: _visible, overflow: _overflow }
    })
  }, [minVisibleItems])

  const isCanaryCrushed = () => canaryRef.current && canaryRef.current.getBoundingClientRect().width < CANARY_WIDTH

  const checkOverflow = useCallback(() => {
    if (isCanaryCrushed()) {
      hideLastItem()
    }
  }, [hideLastItem])

  useLayoutEffect(() => {
    checkOverflow()
  }, [visible, checkOverflow])

  useLayoutEffect(() => {
    resetItems()
  }, [items, resetItems])

  useLayoutEffect(() => {
    if (width === undefined) {
      return
    }

    if (previousWidth.current == undefined) {
      previousWidth.current = width
    }

    if (width > previousWidth.current) {
      resetItems()
    }
    if (width <= previousWidth.current) {
      checkOverflow()
    }

    previousWidth.current = width
  }, [width, resetItems, checkOverflow])

  useLayoutEffect(() => {
    if (!containerRef.current) return

    const observer = new ResizeObserver(() => {
      if (!containerRef.current) return

      const newWidth = containerRef.current.getBoundingClientRect().width
      setWidth(newWidth)
    })

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <Container ref={containerRef} className={className}>
      {itemsRenderer(visible)}
      {overflowRenderer(overflow)}
      <Canary ref={canaryRef} />
    </Container>
  )
}
